// ./src/hooks/useAccountEmails.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  AccountEmail,
  AccountEmailService,
  AccountEmailFilter,
  AccountEmailInput,
  AccountEmailVerifyCodeInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseAccountEmailsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<AccountEmailFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;
}

export interface UseAccountEmailsReturn {
  // Data state
  accountEmail: AccountEmail | null;
  accountEmails: AccountEmail[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<AccountEmailFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  isSendingConfirmation: boolean;
  isVerifyingCode: boolean;
  selectedAccountEmail: AccountEmail | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<AccountEmailFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    shouldReset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<AccountEmail[]>;
  create: (params: AccountEmailInput) => Promise<AccountEmail | null>;
  update: (
    id: string,
    params: AccountEmailInput
  ) => Promise<AccountEmail | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Confirmation operations
  sendConfirmationCode: (id: string) => Promise<AccountEmail | null>;
  verifyCode: (id: string, code: string) => Promise<AccountEmail | null>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // UI state management
  setSelectedAccountEmail: (accountEmail: AccountEmail | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "email", direction: "asc" }];

export const useAccountEmails = (
  options: UseAccountEmailsOptions = {}
): UseAccountEmailsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // Data state
  const [accountEmail, setAccountEmail] = useState<AccountEmail | null>(null);
  const [accountEmails, setAccountEmails] = useState<AccountEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<AccountEmailFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [selectedAccountEmail, setSelectedAccountEmail] =
    useState<AccountEmail | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => accountEmails.length === 0 && !loading,
    [accountEmails.length, loading]
  );

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: AccountEmail) => {
    setAccountEmails((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setAccountEmails((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setAccountEmails((prev) =>
      prev.filter((item) => !idsSet.has(item.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setAccountEmail(null);
    setAccountEmails([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setIsSendingConfirmation(false);
    setIsVerifyingCode(false);
    setSelectedAccountEmail(null);
  }, []);

  // Get single account email (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await AccountEmailService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as AccountEmail[])[0];
          setAccountEmail(data);
          updateOrAddInList(data);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load account email";
          setLastError(errorMessage);
          setAccountEmail(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load account email";
        setLastError(errorMessage);
        setAccountEmail(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // List account emails (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<AccountEmailFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      shouldReset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (shouldReset) {
          setAccountEmails([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await AccountEmailService.list(
          filter as AccountEmailFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as AccountEmail[])
            : [];

          if (isAppending) {
            setAccountEmails((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setAccountEmails(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load account emails";
          setLastError(errorMessage);
          if (!isAppending) setAccountEmails([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load account emails";
        setLastError(errorMessage);
        if (!isAppending) setAccountEmails([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many account emails by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<AccountEmail[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await AccountEmailService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as AccountEmail[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return loadedItems;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load account emails";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load account emails";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // Create account email (matching API method name)
  const create = useCallback(
    async (params: AccountEmailInput): Promise<AccountEmail | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await AccountEmailService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as AccountEmail[])[0];
          setAccountEmail(newItem);
          setAccountEmails((prev) => [newItem, ...prev]);
          setLastError(null);
          return newItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create account email";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create account email";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update account email (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: AccountEmailInput
    ): Promise<AccountEmail | null> => {
      if (!id) {
        setLastError("Account email ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await AccountEmailService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as AccountEmail[])[0];
          setAccountEmail(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update account email";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update account email";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove account email (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Account email ID is required for removal");
        return false;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await AccountEmailService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setAccountEmail(null);
          removeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove account email";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove account email";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many account emails (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one account email ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await AccountEmailService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setAccountEmail(null);
          removeManyFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove account emails";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove account emails";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeManyFromList]
  );

  // Send confirmation code to account email
  const sendConfirmationCode = useCallback(
    async (id: string): Promise<AccountEmail | null> => {
      if (!id) {
        setLastError("Account email ID is required");
        return null;
      }

      setIsSendingConfirmation(true);
      setLastError(null);

      try {
        const result = await AccountEmailService.sendConfirmationCode(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as AccountEmail[])[0];
          setAccountEmail(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to send confirmation code";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to send confirmation code";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsSendingConfirmation(false);
      }
    },
    [updateOrAddInList]
  );

  // Verify confirmation code
  const verifyCode = useCallback(
    async (id: string, code: string): Promise<AccountEmail | null> => {
      if (!id) {
        setLastError("Account email ID is required");
        return null;
      }

      if (!code) {
        setLastError("Confirmation code is required");
        return null;
      }

      setIsVerifyingCode(true);
      setLastError(null);

      try {
        const params: AccountEmailVerifyCodeInput = { id, code };
        const result = await AccountEmailService.verifyCode(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as AccountEmail[])[0];
          setAccountEmail(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to verify confirmation code";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to verify confirmation code";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsVerifyingCode(false);
      }
    },
    [updateOrAddInList]
  );

  // Load more account emails
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh account emails
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  return {
    accountEmail,
    accountEmails,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingId,
    isSendingConfirmation,
    isVerifyingCode,
    selectedAccountEmail,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    sendConfirmationCode,
    verifyCode,
    loadMore,
    refresh,
    setSelectedAccountEmail,
    reset,
    lastError,
    clearError,
  };
};
